/**
 * Dashboard — the operator's shift-start board.
 *
 * Each panel loads independently and only the ones the role can read are rendered, so a
 * support operator gets a clean board rather than a wall of permission errors. The
 * gift-card rail keeps its own callout because flipping it on is the headline operator
 * action.
 *
 * **Everything below the tiles is derived from one bounded window of transactions, and
 * says so.** The transactions endpoint is cursor-paginated (`adminTxFilterSchema`: `kind`,
 * `status`, `userId`, `limit` ≤ 100, `cursor` — there is no aggregate route and none is
 * invented here), so the board walks at most {@link MAX_PAGES} pages and reports on
 * exactly what it fetched. That is why every label reads "in this window" and never
 * "total": the tile this replaced summed one page of transactions and called the result
 * "Settled (recent)", which is a number that changes with page size and means nothing.
 * A dashboard that quietly under-reports money is worse than one that reports less.
 *
 * All money arithmetic is `BigInt` over the minor-unit strings. `Number` never touches an
 * amount — only bar ratios, which are layout, not money.
 */
import { Link } from 'react-router-dom';

import type { AdminApi } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type {
  AdminKycPendingResponse,
  AdminTxKind,
  AdminTxRow,
  AdminTxStatus,
  MoneyDTO,
} from '../contracts/types.js';
import { Badge, Card } from '../components/ui.js';
import { IconGift } from '../components/icons.js';
import { useAsync } from '../hooks/useAsync.js';
import { formatDate, humanize } from '../lib/format.js';
import { formatMoney } from '../lib/money.js';
import { useBrand } from '../branding/BrandProvider.js';

/** Pages of transactions the board will walk before it stops and says it stopped. */
const MAX_PAGES = 5;
/** The contract's ceiling on `limit`, so the window is at most 500 events. */
const PAGE_SIZE = 100;
/** Never render more day-columns than this; the window is short by construction. */
const MAX_TREND_DAYS = 90;

/**
 * The contract's closed currency set. Money panels are denominated in exactly one of
 * these, never a free string, so a typo cannot invent a currency that formats as itself.
 */
type TxCurrency = MoneyDTO['currency'];

interface TxWindow {
  readonly rows: readonly AdminTxRow[];
  /** True when the walk hit `MAX_PAGES` with more still behind the cursor. */
  readonly truncated: boolean;
}

const EMPTY_WINDOW: TxWindow = { rows: [], truncated: false };

/**
 * Walk the cursor for a bounded window.
 *
 * Bounded on purpose: an unbounded walk over a real ledger is a dashboard that takes
 * minutes to paint and a database that notices. Five pages is enough to make a trend
 * legible, and `truncated` is carried out so the page can admit what it did not read
 * rather than implying it read everything.
 */
async function loadTxWindow(api: AdminApi): Promise<TxWindow> {
  const rows: AdminTxRow[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const res = await api.listTransactions({
      limit: PAGE_SIZE,
      ...(cursor === undefined ? {} : { cursor }),
    });
    rows.push(...res.items);
    if (res.nextCursor === null) return { rows, truncated: false };
    cursor = res.nextCursor;
  }
  return { rows, truncated: true };
}

export function DashboardPage(): JSX.Element {
  const { api, session } = useAuth();
  const brand = useBrand();
  const role = session?.me.role ?? 'support';

  const canKyc = adminCan(role, 'kyc:read');
  const canReview = adminCan(role, 'kyc:review');
  const canTx = adminCan(role, 'transactions:read');
  const canSupport = adminCan(role, 'users:read');

  const flags = useAsync(() => api.listFlags(), []);
  const providers = useAsync(() => api.listProviders(), []);
  const support = useAsync(
    () => (canSupport ? api.listInbox() : Promise.resolve({ items: [], nextCursor: null })),
    [canSupport],
  );
  const kyc = useAsync(
    () => (canKyc ? api.kycQueue() : Promise.resolve({ items: [], nextCursor: null })),
    [canKyc],
  );
  /**
   * The KYC queue's watchdog, separate from the queue itself.
   *
   * Its own cheap read on purpose: the depth alone does not say whether anyone is working
   * the queue, and the age of the oldest item does. The routing rules deliberately send
   * every inconclusive automated check to a human, so a queue nobody is watching is the
   * exact failure mode this endpoint exists to catch — which makes it a dashboard number
   * rather than something you only see once you have opened the KYC page.
   */
  const kycHealth = useAsync(
    () => (canKyc ? api.kycPending() : Promise.resolve(null)),
    [canKyc],
  );
  const gift = useAsync(
    () => (canReview ? api.giftcardQueue() : Promise.resolve({ items: [], nextCursor: null })),
    [canReview],
  );
  const tx = useAsync(() => (canTx ? loadTxWindow(api) : Promise.resolve(EMPTY_WINDOW)), [canTx]);

  const giftFlag = flags.data?.flags.find((f) => f.key === 'gift_cards') ?? null;
  const enabledCount = flags.data?.flags.filter((f) => f.enabled).length ?? null;
  const payout = providers.data?.providers.find((p) => p.capability === 'payout') ?? null;
  const activePayout = payout ? payout.priority.filter((p) => !payout.disabled.includes(p)).length : null;
  const pending = canKyc ? kyc.data?.items.length ?? null : null;
  const giftPending = canReview ? gift.data?.items.length ?? null : null;
  const openSupport = canSupport
    ? support.data?.items.filter((t) => t.status === 'open' || t.status === 'pending').length ?? null
    : null;

  const rows = tx.data?.rows ?? [];
  const currency = primaryCurrencyOf(rows);
  const settled = rows.filter((r) => r.status === 'settled');
  const settledTotal: MoneyDTO = { currency, amount: sumMinor(settled, currency).toString() };

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Welcome back, {session?.me.name.split(' ')[0] ?? 'operator'}</h1>
        <p className="page-head__sub">Here is where {brand.platformName} stands right now.</p>
      </div>

      {giftFlag !== null && (
        <Card className="flag-row--feature">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div className="brandmark__glyph" style={{ background: 'linear-gradient(135deg,var(--violet-400),var(--violet-600))' }}>
              <IconGift size={17} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 650, display: 'flex', gap: 10, alignItems: 'center' }}>
                Gift cards rail
                <Badge tone={giftFlag.enabled ? 'green' : 'neutral'} dot>
                  {giftFlag.enabled ? 'Live' : 'Off'}
                </Badge>
              </div>
              <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                {giftFlag.enabled
                  ? 'Customers can redeem gift cards to naira.'
                  : 'Turn this on from Feature flags to open gift-card redemption.'}
              </div>
            </div>
            <Link to="/flags" className="btn btn--sm">Manage flags</Link>
          </div>
        </Card>
      )}

      <div style={{ height: 16 }} />

      <div className="grid grid--stats">
        <StatTile label="Enabled features" value={enabledCount === null ? '–' : String(enabledCount)} meta="feature flags on" to="/flags" />
        {/* `primary` is the vendor actually serving payouts, not whoever sits at the top of
            the list — those differ the moment the top-ranked vendor is disabled or has no
            credentials, and a dashboard naming a rail nobody is on is worse than one naming
            none. It is computed by the same function the router walks, so this tile cannot
            disagree with the providers page. */}
        <StatTile
          label="Active payout providers"
          value={activePayout === null ? '–' : String(activePayout)}
          meta={payout ? `serving: ${payout.primary ?? 'nobody'}` : ''}
          to="/providers"
        />
        {canKyc && (
          <StatTile
            label="Pending KYC"
            // The watchdog's own count when it has answered, because it is the number the
            // SLA verdict beside it was computed from — mixing the two reads could show a
            // depth of zero next to "oldest is two days old".
            value={
              kycHealth.data !== null
                ? String(kycHealth.data.pending)
                : pending === null
                  ? '–'
                  : String(pending)
            }
            meta={kycMeta(kycHealth.data)}
            alert={kycHealth.data?.stale === true}
            to="/kyc"
          />
        )}
        {canReview && (
          <StatTile
            label="Pending gift cards"
            value={giftPending === null ? '–' : String(giftPending)}
            meta="awaiting review"
            to="/giftcards"
          />
        )}
        {canTx && (
          <>
            <StatTile
              label="Settled in window"
              value={tx.data === null ? '–' : formatMoney(settledTotal)}
              meta={`${settled.length} of ${rows.length} events`}
              to="/transactions"
            />
            <StatTile
              label="Needs attention"
              value={
                tx.data === null
                  ? '–'
                  : String(rows.filter((r) => r.status === 'pending' || r.status === 'failed').length)
              }
              meta="pending or failed in window"
              to="/transactions"
            />
          </>
        )}
        {canSupport && (
          <StatTile
            label="Open support threads"
            value={openSupport === null ? '–' : String(openSupport)}
            meta="chats & tickets awaiting reply"
            to="/support"
          />
        )}
      </div>

      {canTx && <TransactionPanels window={tx.data} loading={tx.loading} error={tx.error} currency={currency} />}
    </>
  );
}

/**
 * Everything derived from the transaction window, with its provenance stated once.
 *
 * Split out so the honest window caption sits next to the numbers it qualifies rather
 * than in a footnote nobody reads.
 */
function TransactionPanels({
  window: txWindow,
  loading,
  error,
  currency,
}: {
  window: TxWindow | null;
  loading: boolean;
  error: string | null;
  currency: TxCurrency;
}): JSX.Element {
  const rows = txWindow?.rows ?? [];
  const kinds = byKind(rows, currency);
  const statuses = byStatus(rows);
  const days = byDay(rows, currency);
  const maxKindVolume = kinds.reduce((m, k) => (k.volume > m ? k.volume : m), 0n);

  return (
    <>
      <p className="dash-window">
        {loading
          ? 'Reading the most recent transactions…'
          : error !== null
            ? `Transactions could not be read: ${error}`
            : rows.length === 0
              ? 'No transactions yet.'
              : `Everything below covers the last ${rows.length} transactions${spanOf(rows)}. ` +
                (txWindow?.truncated === true
                  ? `The window stops at ${MAX_PAGES} pages. There is more history behind it.`
                  : 'That is the whole history the API holds.')}
      </p>

      <div className="grid grid--2">
        <Card title="Transactions by type">
          {kinds.length === 0 ? (
            <p className="muted">Nothing in this window.</p>
          ) : (
            <div className="dash-bars">
              {kinds.map((k) => (
                <div className="dash-bar" key={k.kind}>
                  <div className="dash-bar__head">
                    <span className="dash-bar__name">{humanize(k.kind)}</span>
                    <span className="dash-bar__count">{k.count}</span>
                  </div>
                  <div className="dash-bar__track">
                    <span
                      className="dash-bar__fill"
                      style={{ width: `${(ratioOf(k.volume, maxKindVolume) * 100).toFixed(2)}%` }}
                    />
                  </div>
                  <div className="dash-bar__vol num">
                    {formatMoney({ currency, amount: k.volume.toString() })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Status mix">
          {rows.length === 0 ? (
            <p className="muted">Nothing in this window.</p>
          ) : (
            <div className="dash-bars">
              {statuses.map((s) => (
                <div className="dash-bar" key={s.status}>
                  <div className="dash-bar__head">
                    <span className="dash-bar__name">
                      <Badge tone={STATUS_TONES[s.status]} dot>
                        {humanize(s.status)}
                      </Badge>
                    </span>
                    <span className="dash-bar__count">
                      {s.count}
                      <span className="faint"> · {percentLabel(s.count, rows.length)}</span>
                    </span>
                  </div>
                  <div className="dash-bar__track">
                    <span
                      className={`dash-bar__fill dash-bar__fill--${STATUS_TONES[s.status]}`}
                      style={{ width: `${((s.count / rows.length) * 100).toFixed(2)}%` }}
                    />
                  </div>
                  <div className="dash-bar__vol num">
                    {formatMoney({ currency, amount: s.volume.toString() })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div style={{ height: 16 }} />

      <Card
        title="Volume trend"
        actions={<span className="faint" style={{ fontSize: 12 }}>settled &amp; pending, by day, in {currency}</span>}
      >
        <VolumeTrend days={days} currency={currency} />
      </Card>
    </>
  );
}

/**
 * A hand-drawn bar chart, one column per day.
 *
 * Deliberately no charting library: this is ~20 lines of SVG, and a dependency here would
 * be several hundred kilobytes shipped to every operator so that a dozen rectangles could
 * be positioned. `preserveAspectRatio="none"` lets the columns stretch to whatever width
 * the card has — safe because every mark is an axis-aligned fill with no stroke and no
 * corner radius, so nothing can distort.
 */
function VolumeTrend({ days, currency }: { days: readonly DayBucket[]; currency: TxCurrency }): JSX.Element {
  if (days.length === 0) return <p className="muted">Not enough history to draw a trend.</p>;

  const max = days.reduce((m, d) => (d.volume > m ? d.volume : m), 0n);
  const step = 12;
  const barWidth = 8;
  const height = 60;
  const width = Math.max(days.length * step, step);
  const total = days.reduce((sum, d) => sum + d.volume, 0n);

  return (
    <div className="trend">
      <svg
        className="trend__svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Daily volume across ${days.length} days, totalling ${formatMoney({ currency, amount: total.toString() })}.`}
      >
        {days.map((d, index) => {
          // A day with any money at all keeps a visible stub, so "small" never renders as
          // "none" — the two mean very different things on an operations board.
          const scaled = ratioOf(d.volume, max) * (height - 2);
          const barHeight = d.volume > 0n ? Math.max(scaled, 1.5) : 0;
          return (
            <rect
              key={d.day}
              className="trend__bar"
              x={index * step}
              y={height - barHeight}
              width={barWidth}
              height={barHeight}
            >
              <title>
                {`${d.day}: ${d.count} event${d.count === 1 ? '' : 's'}, ${formatMoney({ currency, amount: d.volume.toString() })}`}
              </title>
            </rect>
          );
        })}
        <rect className="trend__axis" x={0} y={height - 0.5} width={width} height={0.5} />
      </svg>
      <div className="trend__labels">
        <span>{formatDate(`${days[0]!.day}T00:00:00.000Z`)}</span>
        <span>{formatDate(`${days[days.length - 1]!.day}T00:00:00.000Z`)}</span>
      </div>
    </div>
  );
}

/**
 * One number and its provenance.
 *
 * `alert` tints the tile amber. Used for exactly one thing today — a KYC queue whose oldest
 * item has passed its service level — and deliberately not offered as a general colour knob:
 * a board where several tiles can go amber is a board where none of them mean anything.
 */
function StatTile({
  label,
  value,
  meta,
  to,
  alert = false,
}: {
  label: string;
  value: string;
  meta: string;
  to: string;
  alert?: boolean;
}): JSX.Element {
  return (
    <Link to={to}>
      <Card className={`stat${alert ? ' stat--alert' : ''}`}>
        <div className="stat__label">{label}</div>
        <div className="stat__value">{value}</div>
        {meta !== '' && <div className="stat__meta">{meta}</div>}
      </Card>
    </Link>
  );
}

/**
 * The KYC tile's second line: not "awaiting review", which says nothing, but how long the
 * front of the queue has been waiting and whether that is past the service level.
 *
 * `stale` and the threshold both come from the API, so this renders the contract's verdict
 * rather than a second opinion computed here that could drift from it.
 */
function kycMeta(health: AdminKycPendingResponse | null): string {
  if (health === null) return 'awaiting review';
  if (health.pending === 0) return 'queue is clear';
  const waiting = relativeAge(health.oldestAgeSeconds);
  return health.stale
    ? `oldest has waited ${waiting}, past the ${Math.round(health.staleAfterSeconds / 3600)}h service level`
    : `oldest has waited ${waiting}`;
}

/** Seconds → a compact age. `Math` on a duration is fine; it is not money. */
function relativeAge(seconds: number | null): string {
  if (seconds === null) return 'no time';
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86_400)}d`;
}

// ── derivation ─────────────────────────────────────────────────────────────────────

const STATUS_TONES: Readonly<Record<AdminTxStatus, 'green' | 'amber' | 'red' | 'neutral'>> = {
  settled: 'green',
  pending: 'amber',
  failed: 'red',
  reversed: 'neutral',
};

/**
 * Sum one currency's rows in minor units.
 *
 * `BigInt`, never `Number`: a kobo total passes 2^53 at ₦90 trillion and there is no
 * reason to discover exactly where a float starts rounding somebody's money. Rows in
 * another currency are skipped rather than added, because adding them would be nonsense.
 */
function sumMinor(rows: readonly AdminTxRow[], currency: TxCurrency): bigint {
  let total = 0n;
  for (const row of rows) {
    if (row.amount.currency === currency) total += BigInt(row.amount.amount);
  }
  return total;
}

/**
 * The currency the window is mostly in — what the money panels are denominated in.
 *
 * The ledger settles in naira, so in practice this is always NGN; it is derived rather
 * than assumed so a window that is not would label itself correctly instead of summing
 * two currencies into one meaningless figure.
 */
function primaryCurrencyOf(rows: readonly AdminTxRow[]): TxCurrency {
  const counts = new Map<TxCurrency, number>();
  for (const row of rows) {
    counts.set(row.amount.currency, (counts.get(row.amount.currency) ?? 0) + 1);
  }
  let best: TxCurrency = 'NGN';
  let bestCount = 0;
  for (const [currency, count] of counts) {
    if (count > bestCount) {
      best = currency;
      bestCount = count;
    }
  }
  return best;
}

/**
 * A 0–1 ratio for a bar's width.
 *
 * The division is done in `BigInt` at 1/10,000 resolution and only the resulting *ratio*
 * — a layout number, not an amount — is handed to `Number`. No money value is ever
 * converted, which is the whole rule.
 */
function ratioOf(value: bigint, max: bigint): number {
  if (max <= 0n) return 0;
  return Number((value * 10000n) / max) / 10000;
}

interface KindBucket {
  readonly kind: AdminTxKind;
  readonly count: number;
  readonly volume: bigint;
}

/** Every kind present in the window, heaviest by volume first. */
function byKind(rows: readonly AdminTxRow[], currency: TxCurrency): KindBucket[] {
  const counts = new Map<AdminTxKind, { count: number; volume: bigint }>();
  for (const row of rows) {
    const bucket = counts.get(row.kind) ?? { count: 0, volume: 0n };
    bucket.count += 1;
    if (row.amount.currency === currency) bucket.volume += BigInt(row.amount.amount);
    counts.set(row.kind, bucket);
  }
  return [...counts.entries()]
    .map(([kind, b]) => ({ kind, count: b.count, volume: b.volume }))
    .sort((a, b) => (a.volume === b.volume ? b.count - a.count : a.volume > b.volume ? -1 : 1));
}

interface StatusBucket {
  readonly status: AdminTxStatus;
  readonly count: number;
  readonly volume: bigint;
}

/**
 * Settled / pending / failed / reversed, in that order, and only what is present.
 *
 * `reversed` is included rather than folded away: it is a distinct outcome in the
 * contract, and a mix that silently dropped it would not add up to the window it claims
 * to describe.
 */
function byStatus(rows: readonly AdminTxRow[]): StatusBucket[] {
  const order: readonly AdminTxStatus[] = ['settled', 'pending', 'failed', 'reversed'];
  const counts = new Map<AdminTxStatus, { count: number; volume: bigint }>();
  for (const row of rows) {
    const bucket = counts.get(row.status) ?? { count: 0, volume: 0n };
    bucket.count += 1;
    bucket.volume += BigInt(row.amount.amount);
    counts.set(row.status, bucket);
  }
  return order
    .filter((status) => counts.has(status))
    .map((status) => {
      const b = counts.get(status)!;
      return { status, count: b.count, volume: b.volume };
    });
}

interface DayBucket {
  /** `YYYY-MM-DD`, UTC — the same day boundary the API's timestamps are written in. */
  readonly day: string;
  readonly count: number;
  readonly volume: bigint;
}

/**
 * One bucket per calendar day, gaps included.
 *
 * A day with no transactions has to render as an empty column, not be skipped: a chart
 * that closes its gaps turns a quiet weekend into a smooth line, which is the opposite of
 * what an operations board is for. Only settled and pending count towards volume —
 * failed and reversed money did not move.
 */
function byDay(rows: readonly AdminTxRow[], currency: TxCurrency): DayBucket[] {
  const counted = rows.filter((r) => r.status === 'settled' || r.status === 'pending');
  if (counted.length === 0) return [];

  const buckets = new Map<string, { count: number; volume: bigint }>();
  for (const row of counted) {
    const day = row.createdAt.slice(0, 10);
    const bucket = buckets.get(day) ?? { count: 0, volume: 0n };
    bucket.count += 1;
    if (row.amount.currency === currency) bucket.volume += BigInt(row.amount.amount);
    buckets.set(day, bucket);
  }

  const present = [...buckets.keys()].sort();
  const first = present[0]!;
  const last = present[present.length - 1]!;
  const days: DayBucket[] = [];
  for (let t = Date.parse(`${first}T00:00:00.000Z`); t <= Date.parse(`${last}T00:00:00.000Z`); t += 86_400_000) {
    const day = new Date(t).toISOString().slice(0, 10);
    const bucket = buckets.get(day);
    days.push({ day, count: bucket?.count ?? 0, volume: bucket?.volume ?? 0n });
  }
  return days.slice(-MAX_TREND_DAYS);
}

/** ` from 12 Jul 2026 to 30 Jul 2026`, or '' when the window has no usable dates. */
function spanOf(rows: readonly AdminTxRow[]): string {
  let earliest: string | null = null;
  let latest: string | null = null;
  for (const row of rows) {
    if (earliest === null || row.createdAt < earliest) earliest = row.createdAt;
    if (latest === null || row.createdAt > latest) latest = row.createdAt;
  }
  if (earliest === null || latest === null) return '';
  return `, from ${formatDate(earliest)} to ${formatDate(latest)}`;
}

function percentLabel(part: number, whole: number): string {
  if (whole === 0) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}
